import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { addDays, DateOnly, daysDiff, PatientTreatmentSettings, TreatmentPlanSettings, TreatmentSettings, TspTreatmentRepository } from '../core';
import { TreatmentProgress } from '../models';
import { BaseService } from './base.service';


/**
 * This service handles treatment plan data.
 */
@Injectable({ providedIn: 'root' })
export class TreatmentService extends BaseService {

	private readonly treatRepo = inject(TspTreatmentRepository);


	/**
	 * Get patient's published treatment plans progress.
	 */
	getPublishedPlansProgress(patientId: string, userId: string): Observable<TreatmentPlanSettings[]> {
		return this.treatRepo.publishedProgressToDate(patientId, userId).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Get treatment plan.
	 */
	getTreatmentPlan(planId: string, userId: string): Observable<TreatmentPlanSettings | null> {
		return this.treatRepo.getTreatmentPlan(planId, userId).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Treatment progress mapper.
	 */
	treatmentProgressMapper(plan: TreatmentPlanSettings | null, consolidationDays: number | null = null): TreatmentProgress | null {
		if (!plan) return null;
		const startConsDate = addDays(plan.finalTreatmentDate, 1);
		return {
			frameId: plan.frameID,
			surgeryDate: new DateOnly(plan.surgeryDate),
			latencyDays: plan.latencyDays,
			startDate: new DateOnly(plan.startTreatmentDate),
			correctionDays: daysDiff(new DateOnly(plan.startTreatmentDate), startConsDate),
			startConsolidationDate: startConsDate,
			consolidationDays: consolidationDays ?? (plan.settings?.consolidationDays ?? 0),
		};
	}

	/**
	 * Update treatment plan settings.
	 */
	updateSettings(planId: string, settings: TreatmentSettings): Observable<void> {
		return this.treatRepo.saveSettings(planId, settings).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Publish treatment plan.
	 */
	publish(caseId: string, settings: PatientTreatmentSettings): Observable<void> {
		return this.treatRepo.publish(caseId, settings).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Revoke treatment plan by plan ID.
	 */
	revoke(planId: string): Observable<void> {
		return this.treatRepo.revoke(planId).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Revoke all treatment plans by patient ID.
	 */
	revokeAll(patientId: string): Observable<void> {
		return this.treatRepo.revokeAll(patientId).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

}
